/**
 * useFocusTrap — Trap keyboard focus inside a container while it is "active".
 *
 * When `active` is true:
 *   • Records `document.activeElement` so we can restore on deactivate.
 *   • Moves focus to the first focusable element inside `containerRef`
 *     (or to the container itself if none exist) on the next frame.
 *   • Captures `Tab` / `Shift+Tab` and cycles within the focusable set,
 *     preventing focus from escaping to the document body.
 * When `active` becomes false:
 *   • Restores focus to the element that was active before the trap was
 *     installed (best-effort — no-op if the element has been unmounted).
 *
 * Designed for dialog / drawer surfaces. Pair with `role="dialog"` and
 * `aria-modal="true"` on the same container.
 *
 * @module hooks/useFocusTrap
 */

import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTORS = [
    'a[href]:not([disabled])',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
].join(',');

interface UseFocusTrapOptions {
    /** A nested dialog owns focus temporarily; retain this trap's restore target. */
    paused?: boolean;
    /**
     * Selector (relative to the container) of an element that should
     * receive focus when the trap activates. If omitted or not found,
     * the first focusable element is used.
     */
    initialFocusSelector?: string;
    /**
     * If true, do not restore focus to the previously-active element on
     * deactivate. Useful when the parent intends to manage focus itself.
     * Default: false.
     */
    skipRestore?: boolean;
}

export function useFocusTrap<T extends HTMLElement>(
    containerRef: React.RefObject<T | null>,
    active: boolean,
    options: UseFocusTrapOptions = {},
): void {
    const previouslyFocusedRef = useRef<HTMLElement | null>(null);
    const { initialFocusSelector, skipRestore = false } = options;
    const pausedRef = useRef(options.paused ?? false);
    pausedRef.current = options.paused ?? false;

    useEffect(() => {
        if (!active) return;
        const container = containerRef.current;
        if (!container) return;

        // Portalled confirmations own keyboard focus even when an older caller
        // has not explicitly paused its underlying drawer.
        const isPaused = () => {
            const owners = document.querySelectorAll<HTMLElement>('[data-modal-focus-owner="true"]');
            const modal = owners.item(owners.length - 1);
            return pausedRef.current || Boolean(modal && !container.contains(modal));
        };

        // Remember the element that had focus so we can restore later.
        previouslyFocusedRef.current = document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null;

        // Move focus into the container on the next frame so any open
        // animation has begun and the element is actually paint-ready.
        const focusInitial = () => {
            if (isPaused()) return;
            const explicit = initialFocusSelector
                ? container.querySelector<HTMLElement>(initialFocusSelector)
                : null;
            const target = explicit
                ?? container.querySelector<HTMLElement>(FOCUSABLE_SELECTORS)
                ?? container;
            if (!target.hasAttribute('tabindex') && target === container) {
                container.setAttribute('tabindex', '-1');
            }
            try {
                target.focus({ preventScroll: true });
            } catch {
                /* ignored — element may have been unmounted */
            }
        };
        const raf = requestAnimationFrame(focusInitial);

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Tab' || isPaused()) return;
            const focusable = Array.from(
                container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS),
            ).filter(el => !el.hasAttribute('aria-hidden') && el.offsetParent !== null);

            if (focusable.length === 0) {
                event.preventDefault();
                container.focus({ preventScroll: true });
                return;
            }

            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            const activeEl = document.activeElement as HTMLElement | null;

            // If focus has already escaped the container, pull it back.
            if (!activeEl || !container.contains(activeEl)) {
                event.preventDefault();
                (event.shiftKey ? last : first).focus({ preventScroll: true });
                return;
            }

            if (event.shiftKey && activeEl === first) {
                event.preventDefault();
                last.focus({ preventScroll: true });
            } else if (!event.shiftKey && activeEl === last) {
                event.preventDefault();
                first.focus({ preventScroll: true });
            }
        };

        document.addEventListener('keydown', handleKeyDown, true);

        return () => {
            cancelAnimationFrame(raf);
            document.removeEventListener('keydown', handleKeyDown, true);

            if (!skipRestore && previouslyFocusedRef.current) {
                const prev = previouslyFocusedRef.current;
                // Only restore if the element is still in the document.
                if (document.contains(prev)) {
                    try {
                        prev.focus({ preventScroll: true });
                    } catch {
                        /* ignored */
                    }
                }
                previouslyFocusedRef.current = null;
            }
        };
    }, [active, containerRef, initialFocusSelector, skipRestore]);
}
